/*
 * ****************************************************************************
 * Copyright (C) 2018-2018, All rights reserved.
 * Project: chessmate
 * Created On: March 23rd, 2018
 * Last Modified: March 23rd, 2018
 * ****************************************************************************
 */

import * as React from 'react';
import { ChessGame as Chess, Move } from '@chess-fu/chess-game';
import Chessboard, { RenderPieceFunction } from '../board/chessboard';
import { ChessRenderInfo, ChessSquare, Dimensions } from '../board/chessTypes';

/** GameProps - Properties */
export interface ChessGameProps {
  fen: string;
  game: Chess;
  className?: string;
  style?: React.CSSProperties;
  flipBoard?: boolean;
  boardSize?: Dimensions;
  highlightMoves?: Move[];
  renderPiece: RenderPieceFunction;
  onMovePiece?: (move: Move) => void | null | Move | Promise<Move>;
}

type GameMoveMap = { [key: string]: Move };

/** ChessGameState - Properties */
export interface ChessGameState {
  selection?: ChessSquare;
  moves?: GameMoveMap;
}

/** Game */
export class ChessGame extends React.Component<ChessGameProps, ChessGameState> {
  private _hasUnloaded = false;

  constructor(props: ChessGameProps, ctx: any) {
    super(props, ctx);
    this.state = {};
  }

  get game(): Chess { return this.props.game; }

  componentWillUnmount() {
    this._hasUnloaded = true;
  }

  componentDidMount() {
  }

  componentDidUpdate(prevProps: ChessGameProps, prevState: ChessGameState, snapshot: any) {
    if (prevProps.fen !== this.props.fen) {
      this.setState({ selection: undefined, moves: {} });
    }
  }


  async move(move: Move) {
    const { onMovePiece } = this.props;
    let moved = true;
    if (onMovePiece) {
      try {
        move = (await Promise.resolve(onMovePiece(move) as any)) || move;
        moved = !!move;
      }
      catch (ex) {
        console.error(ex);
        moved = false;
      }
    }

    if (moved && !this._hasUnloaded) {
      if (!this.props.game) {
        this.game.move(move);
      }
      this.setState({
        selection: undefined,
        moves: {}
      });
    }
  }

  readonly onClickSquare = (e: React.SyntheticEvent<any>) => {
    const game = this.game;
    const { selection } = this.state;
    if (!game.isOngoing()) return;

    const data = e.currentTarget.dataset;
    const square: string = data.square;
    const piece: string | undefined = data.piece;

    if (selection && this.state.moves) {
      const move = this.state.moves[square];
      if (move) {
        this.move(move);
        return;
      }
    }

    if (piece && piece[0] === game.turn()) {
      const moves = (game.moves({ square }) as Move[])
        .reduce<GameMoveMap>((o, m) => { o[m.to] = m; return o; }, {});

      const state = { selection: { file: square[0], rank: parseInt(square[1], 10) } as ChessSquare, moves };
      this.setState(state);
      return;
    }

    if (selection) {
      this.setState({ selection: undefined, moves: {} });
    }
  }

  readonly getSquareProps = (squareData: ChessRenderInfo, props: React.HTMLProps<HTMLDivElement>): React.HTMLProps<HTMLDivElement> => {
    const classes: string[] = [];
    const game = this.game;
    const { selection, moves } = this.state;

    if (selection && squareData.square &&
      squareData.square.file === selection.file &&
      squareData.square.rank === selection.rank) {
      classes.push('selected');
    }
    const key = `${squareData.square.file}${squareData.square.rank}`;
    if (!game.isOngoing()) {
      classes.push('game-over');
    }
    if (!game.isOngoing() && squareData.piece) {
      classes.push('not-allowed');
    }
    else if (selection && moves && moves[key]) {
      classes.push('move-target');
      if (moves[key].captured) {
        classes.push('attack');
      }
    }
    else if (selection && (!squareData.piece || game.turn() !== squareData.piece.color)) {
      classes.push('not-allowed');
    }
    else if (squareData.piece && game.turn() !== squareData.piece.color) {
      classes.push('not-allowed');
    }

    if (!selection && this.props.highlightMoves) {
      const found = this.props.highlightMoves.find(x => x.from === key || x.to === key);
      if (found) {
        classes.push('highlighted');
        if (found.to === key) {
          classes.push('highlighted-target');
        }
      }
    }

    return {
      ...props,
      className: [props.className, ...classes].filter(x => x).join(' '),
      onClick: this.onClickSquare,
    };
  };

  readonly renderPiece = (info: ChessRenderInfo): React.ReactNode => {
    return this.props.renderPiece(info);
  };

  render() {
    const game = this.game;
    const boardSize = this.props.boardSize || { width: 512, height: 512 };
    const { className, style } = this.props;
    // While the board doesn't technically use selection, the getSquareProps method does.
    const props: any = { selection: this.state.selection };

    const boardProps = { ...props.boardProps };
    if (className) {
      boardProps.className = `${className} ${boardProps.className}`;
    }
    if (!game.isOngoing()) {
      // Crashing in the browser
      // boardProps.className = `${boardProps.className} game-over`;
    }
    if (style) {
      boardProps.style = { ...style, ...boardProps.className };
    }

    return (
      <Chessboard
        id="board"
        boardProps={boardProps}
        flipBoard={this.props.flipBoard}
        fen={this.props.fen}
        renderPiece={this.renderPiece}
        squareProps={this.getSquareProps}
        {...props}
        {...boardSize}
      />
    );
  }

}

export default ChessGame;


// WEBPACK FOOTER //
// ./src/components/game/chessGame.tsx